import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Autocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Snackbar,
  InputAdornment
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useCompany } from '../../contexts/CompanyContext';

const STATUS_OPTIONS = [
  'Created',
  'Sent',
  'Approved',
  'Rejected',
  'Converted to Job'
];

export default function EstimateForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { customers, estimates, addEstimate, updateEstimate } = useData();
  const { companyProfile } = useCompany();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [contactInfo, setContactInfo] = useState({
    email: '',
    phone: '',
    streetAddress: '',
    city: '',
    zipCode: ''
  });
  const [formData, setFormData] = useState({
    description: '',
    status: 'Created',
    lineItems: [{ name: '', quantity: 1, unitPrice: 0, description: '' }]
  });

  // Load customer data from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const customerId = params.get('customerId');
    if (customerId && !selectedCustomer) {
      const customer = customers.find(c => c.id === customerId);
      if (customer) {
        setSelectedCustomer(customer);
      }
    }
  }, [location.search, customers, selectedCustomer]);

  // Load estimate data if editing
  useEffect(() => {
    if (id) {
      const estimate = estimates.find(e => e.id === id);
      if (estimate) {
        setFormData(estimate);
        setSelectedCustomer(customers.find(c => c.id === estimate.customerId));
        setContactInfo({
          email: estimate.contactInfo?.email || '',
          phone: estimate.contactInfo?.phone || '',
          streetAddress: estimate.contactInfo?.streetAddress || '',
          city: estimate.contactInfo?.city || '',
          zipCode: estimate.contactInfo?.zipCode || ''
        });
      }
    }
  }, [id, estimates, customers]);

  // Update contact info when customer changes
  useEffect(() => {
    if (selectedCustomer) {
      setContactInfo({
        email: selectedCustomer.email || '',
        phone: selectedCustomer.phone || '',
        streetAddress: selectedCustomer.address || '',
        city: selectedCustomer.city || '',
        zipCode: selectedCustomer.zipCode || ''
      });
    } else {
      setContactInfo({
        email: '',
        phone: '',
        streetAddress: '',
        city: '',
        zipCode: ''
      });
    }
  }, [selectedCustomer]);

  const validateForm = () => {
    const errors = {};
    
    if (!selectedCustomer) {
      errors.customer = 'Please select a customer';
    }

    if (!formData.description.trim()) {
      errors.description = 'Please enter an estimate description';
    }

    if (!formData.lineItems.length) {
      errors.lineItems = 'Please add at least one line item';
    }

    formData.lineItems.forEach((item, index) => {
      if (!item.name.trim()) {
        errors[`lineItem${index}Name`] = 'Item name is required';
      }
      if (item.quantity <= 0) {
        errors[`lineItem${index}Quantity`] = 'Quantity must be greater than 0';
      }
      if (item.unitPrice < 0) {
        errors[`lineItem${index}UnitPrice`] = 'Unit price cannot be negative';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCustomerChange = (event, newValue) => {
    setSelectedCustomer(newValue);
    setValidationErrors(prev => ({ ...prev, customer: null }));
  };

  const handleContactInfoChange = (e) => {
    const { name, value } = e.target;
    setContactInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setValidationErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData.lineItems];
    newLineItems[index] = {
      ...newLineItems[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      lineItems: newLineItems
    }));
    setValidationErrors(prev => ({ 
      ...prev, 
      [`lineItem${index}${field}`]: null 
    }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { name: '', quantity: 1, unitPrice: 0, description: '' }]
    }));
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    return formData.lineItems.reduce((sum, item) => {
      return sum + (item.quantity * item.unitPrice);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!validateForm()) {
      return;
    }

    if (!companyProfile?.companyName) {
      setError('Please set up your company profile in Settings before creating an estimate');
      return;
    }

    setLoading(true);
    try {
    const estimateData = {
      customerId: selectedCustomer.id,
      customerName: selectedCustomer.name,
        description: formData.description,
        status: formData.status,
        lineItems: formData.lineItems,
      contactInfo: contactInfo,
      total: calculateTotal(),
        companyInfo: {
          name: companyProfile.companyName,
          logo: companyProfile.logo,
          address: companyProfile.address,
          phone: companyProfile.phone,
          email: companyProfile.email,
          website: companyProfile.website
        },
      createdBy: 'Admin',
      createdAt: id ? formData.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    if (id) {
        await updateEstimate(id, estimateData);
        setSuccess(true);
        setTimeout(() => navigate(`/estimates/${id}`), 1500);
      } else {
        const newEstimate = await addEstimate(estimateData);
        if (newEstimate && newEstimate.estimateId) {
          setSuccess(true);
          setTimeout(() => navigate(`/estimates/${newEstimate.id}`), 1500);
    } else {
          throw new Error('Failed to create estimate: No ID returned');
        }
      }
    } catch (error) {
      console.error('Error saving estimate:', error);
      setError(error.message || 'Failed to save estimate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, width: '100%' }}>
      <Typography variant="h6" gutterBottom>
          {id ? `Edit Estimate ${formData.estimateId || ''}` : 'New Estimate'}
      </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid 
          container 
            spacing={2} 
          alignItems="center"
          sx={{ mb: 2 }}
        >
            <Grid item xs={12} md={id ? 11 : 12}>
              <Box sx={{ width: '100%' }}>
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => option.name}
                value={selectedCustomer}
                onChange={handleCustomerChange}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email} • {option.phone}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.address}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Select Customer"
                    margin="normal"
                    fullWidth
                      error={!!validationErrors.customer}
                      helperText={validationErrors.customer}
                    InputProps={{
                      ...params.InputProps,
                      style: { 
                        whiteSpace: 'normal'
                      }
                    }}
                    inputProps={{
                      ...params.inputProps,
                      style: { 
                          padding: '8.5px 14px'
                      }
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-root': {
                        height: '56px',
                        width: '100%'
                      }
                    }}
                  />
                )}
                sx={{
                  width: '100%',
                  '& .MuiAutocomplete-popper': {
                      width: '100% !important'
                  }
                }}
                ListboxProps={{
                  style: {
                      maxWidth: '100%'
                  }
                }}
              />
            </Box>
          </Grid>
            {id && (
              <Grid item xs={12} md={1}>
              <TextField
                select
                fullWidth
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                margin="normal"
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              </Grid>
            )}

          {selectedCustomer && (
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={contactInfo.email}
                  onChange={handleContactInfoChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={contactInfo.phone}
                  onChange={handleContactInfoChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  name="streetAddress"
                  value={contactInfo.streetAddress}
                  onChange={handleContactInfoChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Town/City"
                  name="city"
                  value={contactInfo.city}
                  onChange={handleContactInfoChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Zip Code"
                  name="zipCode"
                  value={contactInfo.zipCode}
                  onChange={handleContactInfoChange}
                  margin="normal"
                />
              </Grid>
            </Grid>
          )}
        </Grid>

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Line Items
        </Typography>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer>
              <Table size="small">
            <TableHead>
              <TableRow>
                    <TableCell sx={{ width: { xs: '25%', sm: '30%' } }}>Item Name</TableCell>
                    <TableCell sx={{ width: { xs: '25%', sm: '30%' } }}>Description</TableCell>
                    <TableCell sx={{ width: { xs: '15%', sm: '10%' } }}>Quantity</TableCell>
                    <TableCell sx={{ width: { xs: '15%', sm: '15%' } }}>Unit Price</TableCell>
                    <TableCell sx={{ width: { xs: '10%', sm: '10%' } }}>Total</TableCell>
                    <TableCell sx={{ width: { xs: '10%', sm: '5%' } }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.lineItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      fullWidth
                          size="small"
                      value={item.name}
                      onChange={(e) => handleLineItemChange(index, 'name', e.target.value)}
                          error={!!validationErrors[`lineItem${index}Name`]}
                          helperText={validationErrors[`lineItem${index}Name`]}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                          size="small"
                      multiline
                      rows={2}
                      value={item.description || ''}
                      onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                      placeholder="Item description"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                          size="small"
                      value={item.quantity}
                      onChange={(e) => handleLineItemChange(index, 'quantity', Number(e.target.value))}
                      inputProps={{ min: 1 }}
                          error={!!validationErrors[`lineItem${index}Quantity`]}
                          helperText={validationErrors[`lineItem${index}Quantity`]}
                          sx={{ width: { xs: '60px', sm: '80px' } }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                          size="small"
                      value={item.unitPrice}
                      onChange={(e) => handleLineItemChange(index, 'unitPrice', Number(e.target.value))}
                      inputProps={{ min: 0, step: 0.01 }}
                          error={!!validationErrors[`lineItem${index}UnitPrice`]}
                          helperText={validationErrors[`lineItem${index}UnitPrice`]}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          sx={{ width: { xs: '80px', sm: '100px' } }}
                    />
                  </TableCell>
                  <TableCell>
                    ${(item.quantity * item.unitPrice).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <IconButton
                          size="small"
                      onClick={() => removeLineItem(index)}
                      disabled={formData.lineItems.length === 1}
                    >
                          <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
          </Box>

          {validationErrors.lineItems && (
            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
              {validationErrors.lineItems}
            </Typography>
          )}

        <Button
          startIcon={<AddIcon />}
          onClick={addLineItem}
          sx={{ mt: 2 }}
        >
          Add Line Item
        </Button>

        <Box sx={{ mt: 4 }}>
          <TextField
            fullWidth
            required
            multiline
            rows={3}
            label="Estimate Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
              error={!!validationErrors.description}
              helperText={validationErrors.description}
            placeholder="Enter general description for the entire estimate"
          />
        </Box>

          <Box sx={{ 
            mt: 3, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2
          }}>
          <Typography variant="h6">
            Total: ${calculateTotal().toFixed(2)}
          </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              width: { xs: '100%', sm: 'auto' }
            }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/estimates')}
                fullWidth
                disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
                fullWidth
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {id ? 'Update Estimate' : 'Create Estimate'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>

      <Snackbar
        open={success}
        autoHideDuration={1500}
        onClose={() => setSuccess(false)}
        message={id ? "Estimate updated successfully!" : "Estimate created successfully!"}
      />
    </Box>
  );
} 